import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, AlertTriangle, PlusCircle, X, FileText, Camera, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import IndyClientExport from "@/components/IndyClientExport";
import { EVENT_TYPES, fmtEUR } from "@/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Mark = ({ state }) =>
  state === "found" ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> :
  state === "uncertain" ? <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" /> :
  <PlusCircle className="h-4 w-4 text-slate-400 shrink-0" />;

function addDays(dateStr, days, time) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.toISOString().slice(0, 10)}T${time}`;
}

export default function PdfImport() {
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [creating, setCreating] = useState(false);
  const [dupChoice, setDupChoice] = useState(null);
  const [sug, setSug] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [qualityWarn, setQualityWarn] = useState(null);
  const navigate = useNavigate();
  const pdfInputRef = useRef();
  const camInputRef = useRef();
  const galInputRef = useRef();

  const analyze = async (files) => {
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/reservations/import-document", fd);
      setData(r.data);
      setDupChoice(r.data.duplicates?.length ? null : "none");
      const p = r.data.parsed;
      setForm({
        client: { name: p.client.name || "", phone: p.client.phone || "", email: p.client.email || "", address: p.client.address || "", postal_code: p.client.postcode || "", city: p.client.city || "" },
        event: { date: p.event.date || "", type: p.event.type || "", city: p.event.city || "", postal_code: p.event.postcode || "", address: p.event.address || "" },
        formula: p.pricing.formula || "200",
        price_total: p.pricing.price_total ?? "",
        acompte: p.pricing.acompte ?? "",
        payment_mode: p.pricing.payment_mode || "",
        invoice_number: "",   // n° Webador volontairement ignoré — le n° officiel viendra d'Indy
        doc_date: p.pricing.doc_date || "",
        doc_total: p.pricing.doc_total ?? "",
        toile: p.pricing.toile ? { amount: p.pricing.toile.amount ?? 65, model: p.pricing.toile.model || "" } : null,
        booth_id: "",
        agb_offert: !!(p.extras || {}).agb_offert,
      });
      const bad = (r.data.quality || []).filter((q) => !q.ok);
      if (bad.length) setQualityWarn(bad);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse du document impossible");
    } finally {
      setLoading(false);
    }
  };

  const onPdfFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Déposez un fichier PDF"); return; }
    analyze([file]);
  };

  const onPhotoFile = (file) => {
    if (!file) return;
    analyze([file]);
  };

  const reset = () => { setData(null); setForm(null); setDupChoice(null); setQualityWarn(null); };

  const conf = (key, value) => {
    const c = data?.parsed?.confidence?.[key];
    if (value === "" || value === null || value === undefined) return "missing";
    return c === "uncertain" ? "uncertain" : "found";
  };

  const onCity = (v) => {
    setForm({ ...form, event: { ...form.event, city: v } });
    setShowSug(true);
    if (v.trim().length >= 2) api.get("/cities", { params: { q: v } }).then((r) => setSug(r.data)).catch(() => {});
    else setSug([]);
  };

  const [clientSug, setClientSug] = useState([]);
  const [showClientSug, setShowClientSug] = useState(false);
  const onClientCity = (v) => {
    setForm({ ...form, client: { ...form.client, city: v } });
    setShowClientSug(true);
    if (v.trim().length >= 2) api.get("/cities", { params: { q: v } }).then((r) => setClientSug(r.data)).catch(() => {});
    else setClientSug([]);
  };

  const create = async () => {
    if (!form.client.name.trim()) { toast.error("Le nom du client est obligatoire"); return; }
    if (!form.event.date) { toast.error("La date de l'événement est obligatoire"); return; }
    setCreating(true);
    const t = form.event.type || "Autre";
    const delivery = t === "Mariage" ? addDays(form.event.date, -1, "17:00") : t === "Anniversaire" ? addDays(form.event.date, 0, "09:00") : addDays(form.event.date, 0, "12:00");
    // Dimanche : reprise le soir même (horaire à définir) ; autres jours : lendemain 11h00
    const isSunday = new Date(`${form.event.date}T00:00`).getDay() === 0;
    const pickup = isSunday ? addDays(form.event.date, 0, "19:00") : addDays(form.event.date, 1, "11:00");
    const price = parseFloat(form.price_total) || 0;
    const payload = {
      kind: "photobooth",
      client: form.client,
      event: { date: form.event.date, type: t, type_other: "", time_constraints: "", city: form.event.city, postal_code: form.event.postal_code, address: form.event.address },
      logistics: { booth_id: form.booth_id === "" ? null : Number(form.booth_id), delivery_at: delivery, pickup_at: pickup, stephane: { delivery: false, delivery_cost: "", pickup: false, pickup_cost: "" } },
      pricing: {
        formula: form.formula, formula_price: price, delivery_fee: 0,
        options: form.toile ? [{ label: "Toile de fond", amount: parseFloat(form.toile.amount) || 65, model: form.toile.model || "" }] : [],
        discount: 0, other_fee: 0,
        cabine_fee: 0, consumable_free: false,
        acompte: parseFloat(form.acompte) || 0, payment_mode: form.payment_mode,
        invoice_number: form.invoice_number, doc_date: form.doc_date,
        doc_total: parseFloat(form.doc_total) || 0,
      },
      extras: { cabine_agb: false, agb_offert: form.agb_offert },
      status: "Confirmée",
      notes: "",
      import_id: data.import_id,
    };
    try {
      const r = await api.post("/reservations", payload);
      toast.success("Réservation créée depuis le PDF");
      navigate(`/reservations/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  if (!data) {
    return (
      <div className="space-y-3">
        <div
          data-testid="pdf-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files?.[0];
            if (!f) return;
            f.name.toLowerCase().endsWith(".pdf") ? onPdfFile(f) : onPhotoFile(f);
          }}
          className={`rounded-2xl border-2 border-dashed p-6 sm:p-8 text-center transition-colors ${drag ? "border-sky-500 bg-sky-50" : "border-slate-300 bg-white hover:border-sky-300"}`}
        >
          {loading ? (
            <p className="flex items-center justify-center gap-2 text-sm text-slate-600"><Loader2 className="h-5 w-5 animate-spin text-sky-500" /> Analyse du document en cours…</p>
          ) : (
            <>
              <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <p className="font-heading font-semibold">Glissez votre PDF ou votre photo ici</p>
              <p className="text-xs text-slate-500 mt-1 mb-4">Facture Webador ou autre document : client, acompte et export CSV Indy automatiques. PDF ou photo : même analyse.</p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button type="button" variant="outline" data-testid="import-pdf-button" onClick={() => pdfInputRef.current?.click()}>
                  <FileText className="h-4 w-4 mr-2" /> Importer un PDF
                </Button>
                <Button type="button" data-testid="import-camera-button" onClick={() => camInputRef.current?.click()} className="bg-sky-500 hover:bg-sky-600 text-white transition-colors">
                  <Camera className="h-4 w-4 mr-2" /> Prendre une photo
                </Button>
                <Button type="button" variant="outline" data-testid="import-gallery-button" onClick={() => galInputRef.current?.click()}>
                  <ImageIcon className="h-4 w-4 mr-2" /> Choisir une photo existante
                </Button>
              </div>
            </>
          )}
        </div>
        <input ref={pdfInputRef} type="file" accept="application/pdf" className="hidden" data-testid="pdf-file-input" onChange={(e) => { onPdfFile(e.target.files?.[0]); e.target.value = ""; }} />
        <input ref={camInputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="camera-input" onChange={(e) => { onPhotoFile(e.target.files?.[0]); e.target.value = ""; }} />
        <input ref={galInputRef} type="file" accept="image/*" className="hidden" data-testid="gallery-input" onChange={(e) => { onPhotoFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    );
  }

  const toileAmt = form?.toile ? (parseFloat(form.toile.amount) || 65) : 0;
  const paidDoc = parseFloat(form?.doc_total) || parseFloat(form?.acompte) || 0;
  const solde = (parseFloat(form.price_total) || 0) + toileAmt - paidDoc;
  const dups = data.duplicates || [];

  return (
    <div className="rounded-2xl border-2 border-sky-300 bg-white p-4 sm:p-6 space-y-5" data-testid="pdf-review">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-lg font-bold">Création de la réservation — vérification</h2>
        <Button variant="ghost" size="icon" data-testid="pdf-review-close" onClick={reset}><X className="h-4 w-4" /></Button>
      </div>
      <p className="text-xs text-slate-500 flex items-center gap-1.5">
        <FileText className="h-3.5 w-3.5" /> Document source : {data.source === "pdf" ? "📄 PDF" : `📷 Photo (${data.pages} page${data.pages > 1 ? "s" : ""})`} — ✓ trouvé · ⚠ à confirmer · + à compléter. Tout reste modifiable.
      </p>

      {qualityWarn && (
        <div data-testid="photo-quality-warning" className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2">
          <p className="font-semibold text-amber-900">⚠ La photo semble difficile à lire ({qualityWarn.map((q) => q.issues.join(", ")).join(" · ")}). Vérifiez et complétez les champs ci-dessous.</p>
          <Button size="sm" variant="outline" data-testid="quality-retake" onClick={reset}>Reprendre la photo</Button>
        </div>
      )}

      {dups.length > 0 && dupChoice === null && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid="pdf-duplicate-warning">
          <p className="font-semibold text-amber-900">⚠ Une réservation similaire existe déjà</p>
          {dups.map((d) => <p key={d.id} className="text-sm text-amber-800">{d.client} — {d.date} — {d.city}</p>)}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" data-testid="pdf-dup-open" onClick={() => navigate(`/reservations/${dups[0].id}`)}>Ouvrir la réservation existante</Button>
            <Button size="sm" data-testid="pdf-dup-create" onClick={() => setDupChoice("create")} className="bg-amber-500 hover:bg-amber-600 text-slate-950">Créer quand même</Button>
            <Button size="sm" variant="ghost" data-testid="pdf-dup-cancel" onClick={reset}>Annuler</Button>
          </div>
        </div>
      )}

      {(dupChoice === "create" || dupChoice === "none") && form && (
        <>
          <section className="space-y-3">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500">Client</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["Nom / prénom *", "name", "client_name", "pdf-field-client-name"],
                ["Téléphone", "phone", "client_phone", "pdf-field-client-phone"],
                ["Email", "email", "client_email", "pdf-field-client-email"],
                ["Adresse postale client", "address", "client_address", "pdf-field-client-address"],
              ].map(([label, key, ck, tid]) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf(ck, form.client[key])} /> {label}</label>
                  <Input data-testid={tid} value={form.client[key]} onChange={(e) => setForm({ ...form, client: { ...form.client, [key]: e.target.value } })} />
                </div>
              ))}
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("client_city", form.client.postal_code)} /> Code postal client</label>
                <Input data-testid="pdf-field-client-postcode" value={form.client.postal_code} onChange={(e) => setForm({ ...form, client: { ...form.client, postal_code: e.target.value } })} />
              </div>
              <div className="space-y-1 relative">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("client_city", form.client.city)} /> Ville du client</label>
                <Input data-testid="pdf-field-client-city" value={form.client.city} onChange={(e) => onClientCity(e.target.value)} onFocus={() => setShowClientSug(true)} onBlur={() => setTimeout(() => setShowClientSug(false), 150)} autoComplete="off" />
                {showClientSug && clientSug.length > 0 && (
                  <ul className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-40 overflow-auto" data-testid="pdf-client-city-sug">
                    {clientSug.map((s) => (
                      <li key={s.label}><button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50" onMouseDown={() => { setForm({ ...form, client: { ...form.client, city: s.name, postal_code: s.postcode || form.client.postal_code } }); setClientSug([]); setShowClientSug(false); }}>{s.label}</button></li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <IndyClientExport client={form.client} />
          </section>

          <section className="space-y-3">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500">Événement</h3>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                <Mark state={conf("event_date", form.event.date)} />
                {form.event.date ? `Date événement détectée : ${form.event.date.split("-").reverse().join("/")}` : "Date de l'événement à renseigner"}
              </label>
              <Input data-testid="pdf-field-event-date" type="date" value={form.event.date} onChange={(e) => setForm({ ...form, event: { ...form.event, date: e.target.value } })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("event_type", form.event.type)} /> {form.event.type ? `Type : ${form.event.type}` : "Type d'événement à renseigner"}</label>
              <div className="flex flex-wrap gap-2" data-testid="pdf-type-buttons">
                {EVENT_TYPES.map((t) => (
                  <button key={t} type="button" data-testid={`pdf-type-${t}`} onClick={() => setForm({ ...form, event: { ...form.event, type: t } })}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${form.event.type === t ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:border-sky-400"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1 relative">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("event_city", form.event.city)} /> Ville de l'événement</label>
                <Input data-testid="pdf-field-event-city" value={form.event.city} onChange={(e) => onCity(e.target.value)} onFocus={() => setShowSug(true)} onBlur={() => setTimeout(() => setShowSug(false), 150)} autoComplete="off" />
                {showSug && sug.length > 0 && (
                  <ul className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg max-h-40 overflow-auto">
                    {sug.map((s) => (
                      <li key={s.label}><button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50" onMouseDown={() => { setForm({ ...form, event: { ...form.event, city: s.name, postal_code: s.postcode || form.event.postal_code } }); setSug([]); setShowSug(false); }}>{s.label}</button></li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("event_city", form.event.postal_code)} /> Code postal</label>
                <Input data-testid="pdf-field-event-postcode" value={form.event.postal_code} onChange={(e) => setForm({ ...form, event: { ...form.event, postal_code: e.target.value } })} />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("event_address", form.event.address)} /> Lieu / salle / rue</label>
                <Input data-testid="pdf-field-event-address" value={form.event.address} onChange={(e) => setForm({ ...form, event: { ...form.event, address: e.target.value } })} />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500">Prestation</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("formula", form.formula)} /> Formule</label>
                <select data-testid="pdf-field-formula" value={form.formula} onChange={(e) => setForm({ ...form, formula: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
                  {[["100", "100 photos"], ["200", "200 photos"], ["400", "400 photos"], ["700", "700 photos"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("price_total", form.price_total)} /> Prix total (€)</label>
                <Input data-testid="pdf-field-price-total" type="number" min="0" value={form.price_total} onChange={(e) => setForm({ ...form, price_total: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("acompte", form.acompte)} /> Acompte payé (€)</label>
                <Input data-testid="pdf-field-acompte" type="number" min="0" value={form.acompte} onChange={(e) => setForm({ ...form, acompte: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Solde restant</label>
                <div data-testid="pdf-field-solde" className="h-10 flex items-center px-3 rounded-md bg-slate-100 font-mono tabular-nums text-sm font-semibold">{fmtEUR(solde)}</div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("payment_mode", form.payment_mode)} /> Paiement acompte</label>
                <select data-testid="pdf-field-payment-mode" value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="">—</option>
                  {["Carte", "PayPal", "Virement", "Espèces", "Autre"].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">N° facture Indy (facultatif — jamais repris du document Webador)</label>
                <Input data-testid="pdf-field-invoice-number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="À renseigner plus tard depuis Indy" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("doc_date", form.doc_date)} /> Date du document</label>
                <Input data-testid="pdf-field-doc-date" type="date" value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Mark state={conf("doc_total", form.doc_total)} /> Montant réglé sur le document (€)</label>
                <Input data-testid="pdf-field-doc-total" type="number" min="0" value={form.doc_total} onChange={(e) => setForm({ ...form, doc_total: e.target.value })} />
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid="pdf-toile-block">
              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <input type="checkbox" data-testid="pdf-toile-check" className="h-4 w-4 accent-sky-600"
                  checked={!!form.toile}
                  onChange={(e) => setForm({ ...form, toile: e.target.checked ? { amount: 65, model: "" } : null })} />
                Toile de fond — 65 €
              </label>
              {form.toile && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500">Modèle :</span>
                  {!form.toile.model && <span className="text-xs font-bold text-amber-600" data-testid="pdf-toile-warning">⚠️ Modèle de toile de fond à sélectionner</span>}
                  {[["effet_bois", "Effet bois"], ["paillette", "Paillette"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`pdf-toile-model-${v}`}
                      onClick={() => setForm({ ...form, toile: { ...form.toile, model: v } })}
                      className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${form.toile.model === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:border-sky-400"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="font-heading font-semibold text-sm uppercase tracking-wider text-slate-500">Matériel</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Photobooth</label>
                <select data-testid="pdf-field-booth" value={form.booth_id} onChange={(e) => setForm({ ...form, booth_id: e.target.value })} className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
                  <option value="">À attribuer</option>
                  {[0, 1, 2].map((i) => <option key={i} value={i}>Photobooth {i + 1}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
                <input type="checkbox" data-testid="pdf-field-agb" className="h-4 w-4 accent-sky-600" checked={form.agb_offert} onChange={(e) => setForm({ ...form, agb_offert: e.target.checked })} />
                Audio Guest Book offert
              </label>
            </div>
          </section>

          <Button data-testid="pdf-validate-create" onClick={create} disabled={creating} size="lg" className="w-full bg-sky-500 hover:bg-sky-600 text-white font-semibold transition-colors">
            {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Valider et créer la réservation
          </Button>
        </>
      )}
    </div>
  );
}
